import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK, getDeviceIdAsync, useDeviceId } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type {
  ListMCPsResponse,
  ListMCPToolsResponse,
  RegisterMCPRequest,
  RegisterMCPResponse,
  UpdateMCPRequest,
  UpdateMCPResponse,
  DeleteMCPResponse,
} from '@localagent/sdk';

export function useMCPServers() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const deviceId = useDeviceId();

  // Device-scoped, matching registerMCP/updateMCP/deleteMCP below. The
  // tenant-wide read returns only scope='tenant'|'global' rows and carries no
  // device_reachable, so a server registered here never appeared in this list
  // and the usable/not-usable rendering was unreachable (tools ADR-016).
  return useQuery<ListMCPsResponse, Error>({
    queryKey: ['mcp-servers', deviceId],
    queryFn: () => getSDK().tools.listMCPs(deviceId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && deviceId),
  });
}

export function useMCPTools(mcpId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ListMCPToolsResponse, Error>({
    queryKey: ['mcp-tools', mcpId],
    queryFn: () => getSDK().tools.listTenantMCPTools(mcpId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && mcpId),
  });
}

export function useRegisterMCP() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<RegisterMCPResponse, Error, RegisterMCPRequest>({
    mutationFn: async (request) => getSDK().tools.registerMCP(await getDeviceIdAsync(), request),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
        void queryClient.invalidateQueries({ queryKey: ['tool-catalog'] });
      }
    },
  });
}

export function useUpdateMCP() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<UpdateMCPResponse, Error, { mcpId: string; request: UpdateMCPRequest }>({
    mutationFn: async ({ mcpId, request }) => getSDK().tools.updateMCP(await getDeviceIdAsync(), mcpId, request),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
        void queryClient.invalidateQueries({ queryKey: ['tool-catalog'] });
      }
    },
  });
}

export function useDeleteMCP() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<DeleteMCPResponse, Error, string>({
    mutationFn: async (mcpId) => getSDK().tools.deleteMCP(await getDeviceIdAsync(), mcpId),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
        void queryClient.invalidateQueries({ queryKey: ['tool-catalog'] });
      }
    },
  });
}
